import React, { useEffect, useState } from 'react';
import { CountryPhoneInput } from '@/components/CountryPhoneInput';
import { COUNTRIES, findCountryByCode } from '@/data/countries';

interface Props {
  /** Current value as E.164 (e.g. "+971501234567") or empty. */
  value: string;
  /** Emits the assembled E.164 string ("" if local part is empty). */
  onChange: (e164: string) => void;
  /** ISO-2 default country if value is empty. Defaults to 'AE'. */
  defaultCountry?: string;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * Splits an E.164 string into { countryCode, localDigits } using the longest
 * matching dial code in the COUNTRIES table.
 */
function splitE164(e164: string, fallback = 'AE'): { countryCode: string; number: string } {
  if (!e164 || !e164.startsWith('+')) return { countryCode: fallback, number: (e164 || '').replace(/\D/g, '') };
  // Sort by dial length desc so '+1xxx' (US/CA) doesn't shadow longer codes like '+1809' (DO).
  const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  for (const c of sorted) {
    if (e164.startsWith(c.dial)) {
      return { countryCode: c.code, number: e164.slice(c.dial.length).replace(/\D/g, '') };
    }
  }
  return { countryCode: fallback, number: e164.replace(/\D/g, '') };
}

/**
 * Drop-in phone input that owns the country dropdown internally and exposes
 * just an E.164 string (or empty) to the parent. Use this when the parent
 * stores phones as a single string field.
 */
export const PhoneField: React.FC<Props> = ({
  value, onChange, defaultCountry = 'AE', placeholder, invalid, disabled, className,
}) => {
  const initial = splitE164(value, defaultCountry);
  const [countryCode, setCountryCode] = useState<string>(initial.countryCode);
  const [number, setNumber] = useState<string>(initial.number);

  // Re-sync when the parent forces a new value (e.g. after async load).
  useEffect(() => {
    const next = splitE164(value, defaultCountry);
    setCountryCode(prev => (prev === next.countryCode ? prev : next.countryCode));
    setNumber(prev => (prev === next.number ? prev : next.number));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className={className}>
      <CountryPhoneInput
        countryCode={countryCode}
        number={number}
        defaultCode={defaultCountry}
        invalid={invalid}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(next) => {
          setCountryCode(next.countryCode);
          setNumber(next.number);
          onChange(next.e164);
        }}
      />
      {!number && !disabled && (
        <p className="mt-1 text-2xs text-text-muted">
          {findCountryByCode(countryCode)?.dial} — type number without country code
        </p>
      )}
    </div>
  );
};
